import {
  formatNationalId,
  getNationalIdRules,
  validateNationalId,
} from '../../nationalId';
import { CountryName, ICountryRules } from './interfaces/useRulesByCountry.interface';

export const COUNTRIES: Record<string, CountryName> = {
  chile: 'Chile',
  argentina: 'Argentina',
  brazil: 'Brazil',
  default: 'Default',
};

export const IDENTIFIER_FIELDS = {
  RUT: 'rut',
  CUIL: 'cuil',
  DNI: 'dni',
  CPF: 'cpf',
};

export const RULES_BY = {
  FORM: 'form',
};

export const FORMS_FIELDS = {
  ID_CARD: 'idCard',
  IDENTIFIER: 'identifier',
  FLEX_TIME: 'flexTime',
  PHONE: 'phone',
};

/** @deprecated Prefer {@link formatNationalId}('Chile', value) */
export const formatRut = (rut: string): string => formatNationalId('Chile', rut || '');

const buildPersonalIdRules = (countryKey: string): ICountryRules['form']['identifier'] => {
  const rules = getNationalIdRules(countryKey);
  return {
    label: rules.labelKey,
    pattern: {
      value: /.*/,
      message: rules.invalidKey,
      placeholder: rules.placeholder,
    },
    registerName: FORMS_FIELDS.ID_CARD,
    mask: rules.mask,
    formatIdentifier: (value: string) => rules.format(value || ''),
    validateFunction: (value: string) => validateNationalId(countryKey, value || ''),
  };
};

const getChileRules = (): ICountryRules => ({
  form: {
    idCard: {
      label: 'country.chile.idCard',
      pattern: {
        value: /^(\d{1,2}\.?\d{3}\.?\d{3}-[\dkK])$/,
        message: 'form.format.msg',
        placeholder: '12.345.678-5',
      },
    },
    identifier: buildPersonalIdRules('Chile'),
    flexTime: true,
    phone: {
      label: 'country.chile.phone',
      pattern: {
        message: 'country.phone.invalid',
        placeholder: '+56 2 2123 4567',
      },
    },
  },
});

const getArgentinaRules = (): ICountryRules => ({
  form: {
    idCard: {
      label: 'country.argentina.idCard',
      pattern: {
        value: /^\d{7,8}$/,
        message: 'country.argentina.idCard.invalid',
        placeholder: '12345678',
      },
    },
    identifier: buildPersonalIdRules('Argentina'),
    flexTime: false,
    phone: {
      label: 'country.argentina.phone',
      pattern: {
        message: 'country.phone.invalid',
        placeholder: '+54 9 11 2345-6789',
      },
    },
  },
});

const getBrazilRules = (): ICountryRules => ({
  form: {
    idCard: {
      label: 'country.brazil.idCard',
      pattern: {
        value: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/,
        message: 'country.brazil.idCard.invalid',
        placeholder: '123.456.789-09',
      },
    },
    identifier: buildPersonalIdRules('Brazil'),
    flexTime: false,
    phone: {
      label: 'country.brazil.phone',
      pattern: {
        message: 'country.phone.invalid',
        placeholder: '+55 11 91234-5678',
      },
    },
  },
});

const getDefaultRules = (): ICountryRules => {
  const rules = getNationalIdRules(null);
  return {
    form: {
      idCard: {
        label: rules.labelKey,
        pattern: {
          value: /.*/,
          message: rules.invalidKey,
          placeholder: rules.placeholder,
        },
      },
      identifier: buildPersonalIdRules(null),
      flexTime: false,
      phone: {
        label: 'country.default.phone',
        pattern: {
          message: 'country.phone.invalid',
          placeholder: '',
        },
      },
    },
  };
};

/**
 * Country-specific form labels/masks/validators.
 * Personal document rules come from {@link getNationalIdRules}.
 */
export const countriesFactory = (pCountry?: string): Record<string, ICountryRules> => {
  const country = pCountry ? pCountry.toLowerCase() : COUNTRIES.default.toLowerCase();
  void country;

  return {
    [COUNTRIES.chile]: getChileRules(),
    [COUNTRIES.argentina]: getArgentinaRules(),
    [COUNTRIES.brazil]: getBrazilRules(),
    [COUNTRIES.default]: getDefaultRules(),
  };
};
